import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { makeKindKeyStringFromProps } from '@gonfalon/contexts/kind-key/makeKindKeyString';
import { migrateContextsEpicToReactQuery } from '@gonfalon/dogfood-flags/dogfood-flags';

import { loadContextTarget as loadContextTargetAction } from '#actions/contexts';
import type { ContextItemsEntities } from '#components/Contexts/types';
import { TargetEntry } from '#components/InstructionList/TargetEntry';
import { useResolvedContextItems } from '#hooks/useResolvedContextItems';
import { contextByKindKeySelector, contextRequestByKindKeySelector } from '#reducers/contexts';
import type { GlobalDispatch, GlobalState } from '#reducers/state';
import type { Flag } from '#utils/flags/records/Flag';
import type { TargetsInstructionKind } from '#utils/instructions/targets/types';
import type { UserTargetsInstructionKind } from '#utils/instructions/userTargets/types';
import { ready } from '#utils/reduxUtils';

type TargetEntryContainerProps = {
  instructionKind:
    | TargetsInstructionKind.ADD_TARGETS
    | TargetsInstructionKind.REMOVE_TARGETS
    | TargetsInstructionKind.REPLACE_TARGETS
    | UserTargetsInstructionKind.ADD_USER_TARGETS
    | UserTargetsInstructionKind.REMOVE_USER_TARGETS
    | UserTargetsInstructionKind.REPLACE_USER_TARGETS;
  contextKind: string;
  targetKeys: string[];
  variationId: string;
  flag: Flag;
  loadContextTarget({
    contextKind,
    targetKey,
    projKey,
    envKey,
  }: {
    contextKind: string;
    targetKey: string;
    envKey: string;
    projKey: string;
  }): void;
  contextItemsByKindKey: ContextItemsEntities;
  targetLimit: number;
  isReady: boolean;
  projKey: string;
  envKey: string;
};

function BaseTargetEntryContainer({
  instructionKind,
  contextKind,
  targetKeys,
  variationId,
  flag,
  loadContextTarget,
  contextItemsByKindKey,
  targetLimit,
  isReady,
  projKey,
  envKey,
}: TargetEntryContainerProps) {
  const actualTargetLimit = targetLimit || targetKeys.length;
  const useReactQuery = migrateContextsEpicToReactQuery();

  // React Query path: resolve the visible target keys (with name backfill) in bulk instead of the
  // redux-observable contexts epic. Gated by the migrate-contexts-epic-to-react-query flag. Only keys
  // LaunchDarkly returns land in the map, so unresolved keys stay unlinked — matching the epic path.
  const visibleKeys = useMemo(
    () => (useReactQuery ? targetKeys.slice(0, actualTargetLimit) : []),
    [useReactQuery, targetKeys, actualTargetLimit],
  );
  const { itemsByKindKey: reactQueryContextItemsByKindKey } = useResolvedContextItems({
    projKey,
    envKey,
    contextKind,
    keys: visibleKeys,
    enabled: useReactQuery,
  });

  useEffect(() => {
    if (useReactQuery || isReady) {
      return;
    }
    targetKeys.slice(0, actualTargetLimit).forEach((targetKey) =>
      loadContextTarget({
        contextKind,
        targetKey,
        projKey,
        envKey,
      }),
    );
  }, []);
  return (
    <TargetEntry
      instructionKind={instructionKind}
      contextKind={contextKind}
      targetKeys={targetKeys}
      variationId={variationId}
      flag={flag}
      contextItemsByKindKey={useReactQuery ? reactQueryContextItemsByKindKey : contextItemsByKindKey}
      projKey={projKey}
      envKey={envKey}
      targetLimit={actualTargetLimit}
    />
  );
}

type TargetEntryContainerOwnProps = Pick<
  TargetEntryContainerProps,
  'contextKind' | 'targetKeys' | 'projKey' | 'envKey'
>;
const mapStateToProps = (
  state: GlobalState,
  { contextKind, targetKeys, projKey, envKey }: TargetEntryContainerOwnProps,
) => {
  let isReady = true;
  const contextItemsByKindKey: ContextItemsEntities = {};
  targetKeys.forEach((key) => {
    const kindKey = makeKindKeyStringFromProps(contextKind, key);
    const request = contextRequestByKindKeySelector(state, { kindKey, projKey, envKey });
    isReady = isReady && ready(request);
    const contextItem = contextByKindKeySelector(state, { kindKey, projKey, envKey });
    contextItemsByKindKey[kindKey] = contextItem;
  });
  return {
    contextItemsByKindKey,
    isReady,
  };
};

const mapDispatchToProps = (dispatch: GlobalDispatch) => ({
  loadContextTarget: ({
    contextKind,
    targetKey,
    envKey,
    projKey,
  }: {
    contextKind: string;
    targetKey: string;
    envKey: string;
    projKey: string;
  }) =>
    dispatch(
      loadContextTargetAction({
        viewId: '',
        target: { kind: contextKind, key: targetKey },
        envKey,
        projKey,
      }),
    ),
});

export const TargetEntryContainer = connect(mapStateToProps, mapDispatchToProps)(BaseTargetEntryContainer);
